import React, { useEffect, useState, useMemo, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { createAppTheme } from "./theme";
import PrismDemoOverlay from "./components/demo/PrismDemoOverlay";
import {
  Box,
  CssBaseline,
  Grid,
  Paper,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
  Menu,
  Switch,
  FormControlLabel,
  Tooltip,
  Chip,
  Card,
  CardContent,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import AddIcon from "@mui/icons-material/Add";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BusinessIcon from "@mui/icons-material/Business";
import GroupIcon from "@mui/icons-material/Group";
import SearchIcon from "@mui/icons-material/Search";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ReceiptIcon from "@mui/icons-material/Receipt";
import NotificationsIcon from "@mui/icons-material/Notifications";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PatientCard from "./components/common/PatientCard";
import TabPanel from "./components/common/TabPanel";
import OrganizationsPanel from "./components/admin/OrganizationsPanel";
import SystemSettingsPanel from "./components/admin/SystemSettingsPanel";
import PatientChartContainer from "./features/patientChart/PatientChartContainer";
import AddPatientDialog from "./components/AddPatientDialog";
import AddBillingRecordDialog from "./components/AddBillingRecordDialog";
import QuizzesHub from './features/quizzes/QuizzesHub';
import TakeQuiz from './features/quizzes/TakeQuiz';
import QuizResult from './features/quizzes/QuizResult';
import AdminQuizList from './features/quizzes/AdminQuizList';
import AdminQuizEditor from './features/quizzes/AdminQuizEditor';

const API = process.env.REACT_APP_API_BASE || "https://spectra-api.loafstack.dev";

function UserManagementPanel({ users, onAddUser, onEditUser, onDeleteUser }) {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    role: "user",
    organization: "",
    provider_id: "",
  });

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData(user);
    } else {
      setEditingUser(null);
      setFormData({ name: "", email: "", role: "user", organization: "", provider_id: "" });
    }
    setOpenDialog(true);
  };

  const handleSubmit = () => {
    if (editingUser) {
      onEditUser(editingUser.id, formData);
    } else {
      onAddUser(formData);
    }
    setOpenDialog(false);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">User Management</Typography>
        <Button 
          startIcon={<AddIcon aria-label="Add new user" />} 
          variant="contained" 
          onClick={() => handleOpenDialog()}
          aria-label="Add new user"
        >
          Add User
        </Button>
      </Box>

      <Grid container spacing={2}>
        {users.map((user) => (
          <Grid item xs={12} md={6} lg={4} key={user.id}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                  <Box>
                    <Typography variant="h6">{user.name}</Typography>
                    <Typography color="text.secondary">{user.email}</Typography>
                    <Chip label={user.role} size="small" color="primary" variant="outlined" sx={{ mt: 1 }} />
                  </Box>
                  <Box>
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenDialog(user)}
                      aria-label={`Edit user ${user.name}`}
                    >
                      <EditIcon aria-label={`Edit user ${user.name}`} />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      color="error" 
                      onClick={() => onDeleteUser(user.id)}
                      aria-label={`Delete user ${user.name}`}
                    >
                      <DeleteIcon aria-label={`Delete user ${user.name}`} />
                    </IconButton>
                  </Box>
                </Box>
                {user.organization && (
                  <Typography variant="body2" color="text.secondary">
                    Org: {user.organization}
                  </Typography>
                )}
                {user.provider_id && (
                  <Typography variant="body2" color="text.secondary">
                    Provider ID: {user.provider_id}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? "Edit User" : "Add New User"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            aria-label="User name"
            data-field-name="user_name"
            data-testid="user-name-input"
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            margin="normal"
            aria-label="User email"
            data-field-name="user_email"
            data-testid="user-email-input"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              aria-label="User role selection"
              data-field-name="user_role"
              data-testid="user-role-select"
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="provider">Provider</MenuItem>
              <MenuItem value="nurse">Nurse</MenuItem>
              <MenuItem value="user">User</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Organization"
            value={formData.organization}
            onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
            margin="normal"
            aria-label="User organization"
            data-field-name="user_organization"
            data-testid="user-organization-input"
          />
          <TextField
            fullWidth
            label="Provider ID"
            value={formData.provider_id}
            onChange={(e) => setFormData({ ...formData, provider_id: e.target.value })}
            margin="normal"
            aria-label="User provider ID"
            data-field-name="user_provider_id"
            data-testid="user-provider-id-input"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingUser ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default function App() {
  const [colorMode, setColorMode] = useState(() => {
    return localStorage.getItem("spectra_color_mode") || "dark";
  });

  const toggleColorMode = useCallback(() => {
    setColorMode((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      localStorage.setItem("spectra_color_mode", next);
      return next;
    });
  }, []);

  const theme = useMemo(() => createAppTheme(colorMode), [colorMode]);

  return (
    <Router>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <PrismDemoOverlay>
          <Routes>
            <Route path="/" element={<AppContent colorMode={colorMode} toggleColorMode={toggleColorMode} />} />
            <Route path="/patients" element={<AppContent colorMode={colorMode} toggleColorMode={toggleColorMode} />} />
            <Route path="/patient/:patientId" element={<AppContent colorMode={colorMode} toggleColorMode={toggleColorMode} />} />
            <Route path="/organizations" element={<AppContent colorMode={colorMode} toggleColorMode={toggleColorMode} />} />
            <Route path="/care-plans" element={<AppContent colorMode={colorMode} toggleColorMode={toggleColorMode} />} />
            <Route path="/billing" element={<AppContent colorMode={colorMode} toggleColorMode={toggleColorMode} />} />
            <Route path="/reports" element={<AppContent colorMode={colorMode} toggleColorMode={toggleColorMode} />} />
            <Route path="/admin" element={<AppContent colorMode={colorMode} toggleColorMode={toggleColorMode} />} />
            <Route path="/quizzes" element={<QuizzesHub />} />
            <Route path="/quizzes/:quizId" element={<TakeQuiz />} />
            <Route path="/quizzes/:quizId/result/:attemptId" element={<QuizResult />} />
            <Route path="/admin/quizzes" element={<AdminQuizList />} />
            <Route path="/admin/quizzes/:quizId/edit" element={<AdminQuizEditor />} />
          </Routes>
        </PrismDemoOverlay>
      </ThemeProvider>
    </Router>
  );
}

function AppContent({ colorMode = "dark", toggleColorMode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientId } = useParams();
  
  const [patients, setPatients] = useState([]);
  const [users, setUsers] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [openOrgDialog, setOpenOrgDialog] = useState(false);
  const [orgFormData, setOrgFormData] = useState({});
  const [orgSearchQuery, setOrgSearchQuery] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationMenuAnchor, setNotificationMenuAnchor] = useState(null);
  const [billingRecords, setBillingRecords] = useState([]);
  const [billingStats, setBillingStats] = useState({ total: 0, paid: 0, pending: 0 });
  const [reportData, setReportData] = useState(null);
  const [reportType, setReportType] = useState("");
  const [openReportDialog, setOpenReportDialog] = useState(false);
  const [addPatientDialogOpen, setAddPatientDialogOpen] = useState(false);
  const [addBillingDialogOpen, setAddBillingDialogOpen] = useState(false);
  const [reportSummary, setReportSummary] = useState(null);
  const [adminTab, setAdminTab] = useState(0);

  // Determine current view from URL path
  const getCurrentView = () => {
    const path = location.pathname;
    if (path.startsWith('/patient/')) return 'patientChart';
    if (path === '/patients') return 'patientList';
    if (path === '/organizations') return 'organizations';
    if (path === '/care-plans') return 'carePlans';
    if (path === '/billing') return 'billing';
    if (path === '/reports') return 'reports';
    if (path === '/admin') return 'admin';
    return 'patientList';
  };

  const currentView = getCurrentView();
  const selectedPatientId = patientId ? parseInt(patientId) : null;
  const selectedPatient = patients.find((p) => p.id === selectedPatientId) || null;

  useEffect(() => {
    loadPatients();
    loadUsers();
    loadOrganizations();
    loadNotifications();
    loadBillingData();
  }, []);

  // Refresh report counts each time the Reports view opens
  useEffect(() => {
    if (currentView !== "reports") return;
    fetch(`${API}/reports/dashboard-summary`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => setReportSummary(data))
      .catch((error) => {
        console.error("Failed to load report summary:", error);
        setReportSummary(null);
      });
  }, [currentView]);

  const loadPatients = async () => {
    try {
      const response = await fetch(`${API}/patients`);
      const data = await response.json();
      setPatients(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load patients:", error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await fetch(`${API}/users`);
      const data = await response.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load users:", error);
      setUsers([
        {
          id: 1,
          name: "Dr. Sarah Wilson",
          email: "sarah@spectra.com",
          role: "provider",
          organization: "Spectra Health",
          provider_id: "PRV001",
        },
        {
          id: 2,
          name: "Nurse John Smith",
          email: "john@spectra.com",
          role: "nurse",
          organization: "Spectra Health",
          provider_id: "",
        },
        {
          id: 3,
          name: "Admin Jane Doe",
          email: "jane@spectra.com",
          role: "admin",
          organization: "Spectra Health",
          provider_id: "",
        },
      ]);
    }
  };

  const loadOrganizations = async () => {
    try {
      const response = await fetch(`${API}/organizations`);
      const data = await response.json();
      setOrganizations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load organizations:", error);
      setOrganizations([]);
    }
  };

  const loadNotifications = async () => {
    try {
      const response = await fetch(`${API}/notifications`)  // team feed: Spectra has no login;
      const data = await response.json();
      const list = Array.isArray(data) ? data : [];
      setNotifications(list);
      setUnreadCount(list.filter((n) => !n.is_read).length);
    } catch (error) {
      console.error("Failed to load notifications:", error);
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  const loadBillingData = async () => {
    try {
      const response = await fetch(`${API}/billing-records?limit=100`);
      const data = await response.json();
      const records = Array.isArray(data) ? data : [];
      setBillingRecords(records);

      const total = records.reduce((sum, r) => sum + (r.amount || 0), 0);
      const paid = records.reduce((sum, r) => sum + (r.insurance_paid || 0), 0);
      const pending = records
        .filter((r) => ["pending", "submitted"].includes(r.status))
        .reduce((sum, r) => sum + (r.amount || 0), 0);

      setBillingStats({ total, paid, pending });
    } catch (error) {
      console.error("Failed to load billing data:", error);
      setBillingRecords([]);
      setBillingStats({ total: 0, paid: 0, pending: 0 });
    }
  };

  const handlePatientSelect = (patient) => {
    navigate(`/patient/${patient.id}`);
  };

  const handleAddUser = async (userData) => {
    try {
      const response = await fetch(`${API}/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
      });
      if (response.ok) {
        const newUser = await response.json();
        setUsers([...users, newUser]);
        setSnackbar({ open: true, message: "User added successfully", severity: "success" });
      } else {
        const error = await response.json();
        const errorMsg = Array.isArray(error.detail)
          ? error.detail.map((e) => e.msg).join(", ")
          : error.detail || "Failed to add user";
        setSnackbar({ open: true, message: errorMsg, severity: "error" });
      }
    } catch (error) {
      console.error("Failed to add user:", error);
      setSnackbar({ open: true, message: "Failed to add user", severity: "error" });
    }
  };

  const handleEditUser = async (userId, userData) => {
    try {
      const response = await fetch(`${API}/users/${userId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
      });
      if (response.ok) {
        const updatedUser = await response.json();
        setUsers(users.map((user) => (user.id === userId ? updatedUser : user)));
        setSnackbar({ open: true, message: "User updated successfully", severity: "success" });
      } else {
        const error = await response.json();
        const errorMsg = Array.isArray(error.detail)
          ? error.detail.map((e) => e.msg).join(", ")
          : error.detail || "Failed to update user";
        setSnackbar({ open: true, message: errorMsg, severity: "error" });
      }
    } catch (error) {
      console.error("Failed to update user:", error);
      setSnackbar({ open: true, message: "Failed to update user", severity: "error" });
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const response = await fetch(`${API}/users/${userId}`, {
        method: "DELETE",
      });
      if (response.ok) {
        setUsers(users.filter((user) => user.id !== userId));
        setSnackbar({ open: true, message: "User deleted successfully", severity: "success" });
      } else {
        const error = await response.json();
        const errorMsg = Array.isArray(error.detail)
          ? error.detail.map((e) => e.msg).join(", ")
          : error.detail || "Failed to delete user";
        setSnackbar({ open: true, message: errorMsg, severity: "error" });
      }
    } catch (error) {
      console.error("Failed to delete user:", error);
      setSnackbar({ open: true, message: "Failed to delete user", severity: "error" });
    }
  };

  const handleOpenOrgDialog = (org = null) => {
    if (org) {
      setSelectedOrg(org);
      setOrgFormData({
        name: org.name || "",
        type: org.type || "",
        address: org.address || "",
        phone: org.phone || "",
        email: org.email || "",
        feature_flags: org.feature_flags || {
          care_plans: true,
          billing: true,
          reports: true,
          ai_assistant: true,
          preventive_care: true,
        },
      });
    } else {
      setSelectedOrg(null);
      setOrgFormData({
        name: "",
        type: "hospital",
        address: "",
        phone: "",
        email: "",
        feature_flags: {
          care_plans: true,
          billing: true,
          reports: true,
          ai_assistant: true,
          preventive_care: true,
        },
      });
    }
    setOpenOrgDialog(true);
  };

  const handleCloseOrgDialog = () => {
    setOpenOrgDialog(false);
    setSelectedOrg(null);
    setOrgFormData({});
  };

  const handleSaveOrganization = async () => {
    try {
      const url = selectedOrg ? `${API}/organizations/${selectedOrg.id}` : `${API}/organizations`;
      const method = selectedOrg ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(orgFormData),
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: selectedOrg ? "Organization updated" : "Organization created",
          severity: "success",
        });
        handleCloseOrgDialog();
        loadOrganizations();
      } else {
        setSnackbar({ open: true, message: "Failed to save organization", severity: "error" });
      }
    } catch (error) {
      console.error("Failed to save organization:", error);
      setSnackbar({ open: true, message: "Failed to save organization", severity: "error" });
    }
  };

  const handleAssignPatientToOrg = async (patientId, orgId) => {
    try {
      const response = await fetch(`${API}/patients/${patientId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ organization_id: orgId }),
      });

      if (response.ok) {
        setSnackbar({ open: true, message: "Patient assigned to organization", severity: "success" });
        loadPatients();
      } else {
        setSnackbar({ open: true, message: "Failed to assign patient", severity: "error" });
      }
    } catch (error) {
      console.error("Failed to assign patient:", error);
      setSnackbar({ open: true, message: "Failed to assign patient", severity: "error" });
    }
  };

  const handleMarkNotificationRead = async (notificationId) => {
    try {
      const response = await fetch(`${API}/notifications/${notificationId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_read: true }),
      });

      if (response.ok) {
        loadNotifications();
      }
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const fetchReport = async (type) => {
    try {
      setSnackbar({ open: true, message: "Generating report...", severity: "info" });

      const response = await fetch(`${API}/reports/${type}`);

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
        setReportType(type);
        setOpenReportDialog(true);
        setSnackbar({ open: true, message: "Report generated successfully", severity: "success" });
      } else {
        setSnackbar({ open: true, message: "Failed to generate report", severity: "error" });
      }
    } catch (error) {
      console.error("Failed to fetch report:", error);
      setSnackbar({ open: true, message: "Failed to generate report", severity: "error" });
    }
  };

  const exportReportToCSV = () => {
    if (!reportData) return;

    let csvContent = "";
    let filename = "report.csv";

    if (reportType === "patients-by-organization") {
      filename = "patients_by_organization.csv";
      csvContent = "Organization,Patient Count\n";
      reportData.forEach((row) => {
        csvContent += `"${row.organization_name}",${row.patient_count}\n`;
      });
    } else if (reportType === "overdue-preventive-care") {
      filename = "overdue_preventive_care.csv";
      csvContent = "Patient,Care Item,Due Date,Status\n";
      reportData.forEach((row) => {
        csvContent += `"${row.patient_name}","${row.care_item}","${row.due_date}","${row.status}"\n`;
      });
    } else if (reportType === "care-gaps-summary") {
      filename = "outstanding_items_summary.csv";
      csvContent = "Category,Count\n";
      Object.entries(reportData).forEach(([key, value]) => {
        csvContent += `"${key}",${value}\n`;
      });
    }

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setSnackbar({ open: true, message: "Report exported to CSV", severity: "success" });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const onShowSnackbar = (value) => {
    setSnackbar(value);
  };

  const handlePatientAdded = (newPatient) => {
    setPatients(prev => [newPatient, ...prev]);
    setSnackbar({ open: true, message: "Patient added successfully", severity: "success" });
    if (newPatient?.id) navigate(`/patient/${newPatient.id}`);
  };

  const handleDeletePatient = async (patientId) => {
    if (!window.confirm("Are you sure you want to delete this patient? This action cannot be undone.")) {
      return;
    }

    try {
      const response = await fetch(`${API}/patients/${patientId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setPatients(prev => prev.filter(p => p.id !== patientId));
        if (selectedPatientId === patientId) {
          // If the deleted patient is currently selected in the URL, navigate back to the patient list
          navigate('/patients');
        }
        setSnackbar({ open: true, message: "Patient deleted successfully", severity: "success" });
      } else {
        const errorData = await response.json();
        setSnackbar({ open: true, message: errorData.detail || "Failed to delete patient", severity: "error" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Network error. Please try again.", severity: "error" });
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: colorMode === 'dark'
          ? "radial-gradient(1200px 600px at 20% -10%, rgba(67, 137, 154, 0.68) 0%, transparent 60%), radial-gradient(1200px 600px at 110% 10%, rgba(154, 83, 143, 0.6) 0%, transparent 60%), linear-gradient(180deg,#0b1220 0%, rgba(52, 36, 65, 1) 100%)"
          : "linear-gradient(180deg, #e8f4f8 0%, #f0f4f8 100%)",
        p: 2,
      }}
    >
      <Paper sx={{ p: { xs: 1, sm: 1.5, md: 2 }, mb: 2, overflow: 'hidden' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ gap: 1, minWidth: 0 }}>
          {/* Title — fixed min width so nav never squishes it to nothing */}
          <Box sx={{ minWidth: { xs: 80, sm: 110, md: 130 }, flexShrink: 0, overflow: 'hidden' }}>
            <Typography
              fontWeight={600}
              sx={{ fontSize: { xs: '0.9rem', sm: '1.05rem', md: '1.2rem', lg: '1.5rem' }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              Spectra EMR System
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: { xs: 'none', lg: 'block' }, fontSize: '0.75rem' }}
            >
              {selectedPatient ? `${selectedPatient.name} — MRN ${selectedPatient.mrn}` : 'Electronic Medical Records Management'}
            </Typography>
          </Box>

          {/* Nav — scrollable row, never wraps */}
          <Box
            display="flex"
            gap={0.75}
            alignItems="center"
            sx={{ flexShrink: 0, overflowX: 'auto', pb: 0.25,
              '&::-webkit-scrollbar': { height: 3 },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 },
            }}
          >
            <Tooltip title="Notifications">
              <IconButton
                onClick={(e) => setNotificationMenuAnchor(e.currentTarget)}
                color="inherit"
                size="small"
                data-testid="notifications-button"
                aria-label={`Notifications (${unreadCount} unread)`}
              >
                <Badge badgeContent={unreadCount} color="error">
                  <NotificationsIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title={colorMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
              <IconButton onClick={toggleColorMode} color="inherit" size="small" aria-label="Toggle color mode">
                {colorMode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
              </IconButton>
            </Tooltip>

            <Button size="small" variant={currentView === "patientList" ? "contained" : "outlined"} onClick={() => navigate('/patients')} startIcon={<ListAltIcon />} data-testid="nav-patient-list" aria-label="Navigate to Patient List view" sx={{ whiteSpace: 'nowrap' }}>
              Patient List
            </Button>
            <Button size="small" variant="contained" color="success" onClick={() => setAddPatientDialogOpen(true)} startIcon={<AddIcon />} data-testid="nav-add-patient" aria-label="Add new patient" sx={{ whiteSpace: 'nowrap' }}>
              Add Patient
            </Button>
            <Button size="small" variant={currentView === "patientChart" ? "contained" : "outlined"} onClick={() => navigate(selectedPatientId ? `/patient/${selectedPatientId}` : '/patients')} startIcon={<PersonIcon />} data-testid="nav-patient-chart" aria-label="Navigate to Patient Chart view" sx={{ whiteSpace: 'nowrap' }}>
              Patient Chart
            </Button>
            <Button size="small" variant={currentView === "organizations" ? "contained" : "outlined"} onClick={() => navigate('/organizations')} startIcon={<BusinessIcon />} data-testid="nav-organizations" aria-label="Navigate to Organizations view" sx={{ whiteSpace: 'nowrap' }}>
              Organizations
            </Button>
            <Button size="small" variant={currentView === "carePlans" ? "contained" : "outlined"} onClick={() => navigate('/care-plans')} startIcon={<ListAltIcon />} data-testid="nav-care-plans" aria-label="Navigate to Care Plans view" sx={{ whiteSpace: 'nowrap' }}>
              Care Plans
            </Button>
            <Button size="small" variant={currentView === "billing" ? "contained" : "outlined"} onClick={() => navigate('/billing')} startIcon={<ReceiptIcon />} data-testid="nav-billing" aria-label="Navigate to Billing view" sx={{ whiteSpace: 'nowrap' }}>
              Billing
            </Button>
            <Button size="small" variant={currentView === "reports" ? "contained" : "outlined"} onClick={() => navigate('/reports')} startIcon={<AssessmentIcon />} data-testid="nav-reports" aria-label="Navigate to Reports view" sx={{ whiteSpace: 'nowrap' }}>
              Reports
            </Button>
            <Button size="small" variant={currentView === "admin" ? "contained" : "outlined"} onClick={() => navigate('/admin')} startIcon={<AdminPanelSettingsIcon />} data-testid="nav-admin" aria-label="Navigate to Admin view" sx={{ whiteSpace: 'nowrap' }}>
              Admin
            </Button>
            <Button size="small" variant={currentView === "quizzes" ? "contained" : "outlined"} onClick={() => navigate('/quizzes')} data-testid="nav-quizzes" aria-label="Navigate to Quizzes" sx={{ whiteSpace: 'nowrap' }}>Quizzes</Button>
          </Box>
        </Box>
      </Paper>

        <Box sx={{ width: "100%", px: 2 }}>
          {currentView === "patientList" && (
            <Grid container spacing={2}>
              {patients.map((patient) => {
                const organization = organizations.find((o) => o.id === patient.organization_id) || null;
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={patient.id}>
                    <PatientCard
                      patient={patient}
                      onClick={handlePatientSelect}
                      selected={patient.id === selectedPatientId}
                      organization={organization}
                      onDelete={handleDeletePatient}
                    />
                  </Grid>
                );
              })}
            </Grid>
          )}

          {currentView === "admin" && (
            <Paper sx={{ width: "100%" }}>
              <Tabs value={adminTab} onChange={(e, newValue) => setAdminTab(newValue)}>
                <Tab label="User Management" icon={<GroupIcon />} />
                <Tab label="Organizations" icon={<BusinessIcon />} />
                <Tab label="System Settings" icon={<AdminPanelSettingsIcon />} />
              </Tabs>

              <TabPanel value={adminTab} index={0}>
                <UserManagementPanel
                  users={users}
                  onAddUser={handleAddUser}
                  onEditUser={handleEditUser}
                  onDeleteUser={handleDeleteUser}
                />
              </TabPanel>

              <TabPanel value={adminTab} index={1}>
                <OrganizationsPanel apiBase={API} organizations={organizations} onChanged={loadOrganizations} />
              </TabPanel>

              <TabPanel value={adminTab} index={2}>
                <SystemSettingsPanel apiBase={API} />
              </TabPanel>
            </Paper>
          )}

          {currentView === "patientChart" && selectedPatient && (
            <PatientChartContainer
              patient={selectedPatient}
              patients={patients}
              apiBase={API}
              onShowSnackbar={onShowSnackbar}
            />
          )}

          {currentView === "patientChart" && !selectedPatient && (
            <Paper sx={{ p: 3, textAlign: "center" }}>
              <PersonIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No patient selected
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Use the Patient List view to select a patient
              </Typography>
              <Button 
                variant="contained" 
                onClick={() => navigate('/patients')} 
                sx={{ mt: 2 }}
                aria-label="Go to Patient List"
              >
                Go to Patient List
              </Button>
            </Paper>
          )}

          {currentView === "organizations" && (
            <Paper sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5">Organization Management</Typography>
                <Box display="flex" gap={2}>
                  <TextField
                    size="small"
                    placeholder="Search organizations..."
                    value={orgSearchQuery}
                    onChange={(e) => setOrgSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    aria-label="Search organizations"
                    data-field-name="organization_search"
                    data-testid="organization-search-input"
                  />
                  <Button 
                    variant="contained" 
                    startIcon={<AddIcon aria-label="Add new organization" />} 
                    onClick={() => handleOpenOrgDialog()}
                    aria-label="Add new organization"
                  >
                    Add Organization
                  </Button>
                </Box>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Organization</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Type</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Contact</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Patients</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Feature Flags</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>Actions</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {organizations
                          .filter(
                            (org) =>
                              !orgSearchQuery ||
                              org.name?.toLowerCase().includes(orgSearchQuery.toLowerCase()) ||
                              org.type?.toLowerCase().includes(orgSearchQuery.toLowerCase())
                          )
                          .map((org) => {
                            const orgPatients = patients.filter((p) => p.organization_id === org.id);
                            const flags = org.feature_flags || {};
                            const activeFlags = Object.entries(flags)
                              .filter(([, value]) => value)
                              .map(([key]) => key);

                            return (
                              <TableRow key={org.id} hover>
                                <TableCell>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <BusinessIcon color="primary" />
                                    <Box>
                                      <Typography variant="body2" fontWeight={600}>
                                        {org.name}
                                      </Typography>
                                      {org.address && (
                                        <Typography variant="caption" color="text.secondary">
                                          {org.address}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={org.type || "N/A"}
                                    size="small"
                                    color={org.type === "hospital" ? "primary" : "default"}
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  {org.phone && (
                                    <Typography variant="caption" display="block">
                                      📞 {org.phone}
                                    </Typography>
                                  )}
                                  {org.email && (
                                    <Typography variant="caption" display="block">
                                      📧 {org.email}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={`${orgPatients.length} patients`}
                                    size="small"
                                    icon={<GroupIcon />}
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                                    {activeFlags.length > 0 ? (
                                      activeFlags.slice(0, 3).map((flag) => (
                                        <Chip
                                          key={flag}
                                          label={flag.replace(/_/g, " ")}
                                          size="small"
                                          color="success"
                                          variant="outlined"
                                        />
                                      ))
                                    ) : (
                                      <Typography variant="caption" color="text.secondary">
                                        No features enabled
                                      </Typography>
                                    )}
                                    {activeFlags.length > 3 && (
                                      <Chip label={`+${activeFlags.length - 3}`} size="small" color="default" variant="outlined" />
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton 
                                    size="small" 
                                    color="primary" 
                                    onClick={() => handleOpenOrgDialog(org)}
                                    aria-label={`Edit organization ${org.name}`}
                                  >
                                    <EditIcon aria-label={`Edit organization ${org.name}`} />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Assign Patients to Organizations
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Use the dropdown to assign patients to organizations
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <strong>Patient</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Current Organization</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Assign To</strong>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {patients.slice(0, 10).map((patient) => {
                            const currentOrg = organizations.find((o) => o.id === patient.organization_id);
                            return (
                              <TableRow key={patient.id}>
                                <TableCell>
                                  <Typography variant="body2">{patient.name}</Typography>
                                </TableCell>
                                <TableCell>
                                  {currentOrg ? (
                                    <Chip label={currentOrg.name} size="small" color="primary" variant="outlined" />
                                  ) : (
                                    <Chip label="Unassigned" size="small" variant="outlined" />
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Select
                                    size="small"
                                    value={patient.organization_id || ""}
                                    onChange={(e) => handleAssignPatientToOrg(patient.id, e.target.value)}
                                    displayEmpty
                                    sx={{ minWidth: 200 }}
                                    aria-label={`Assign patient ${patient.name} to organization`}
                                    data-field-name="patient_organization_assignment"
                                    data-patient-id={patient.id}
                                    data-testid={`patient-org-assign-${patient.id}`}
                                  >
                                    <MenuItem value="">
                                      <em>Unassigned</em>
                                    </MenuItem>
                                    {organizations.map((org) => (
                                      <MenuItem key={org.id} value={org.id}>
                                        {org.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          )}

          {currentView === "carePlans" && (
            <Paper sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5">Care Plans</Typography>
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon aria-label="Add new care plan" />} 
                  onClick={() => navigate('/patients')}
                  aria-label="Create Care Plan - Go to Patient Chart"
                >
                  Create Care Plan
                </Button>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Alert severity="info">
                    Care Plans help coordinate patient care with goals, interventions, and outcomes tracking.
                  </Alert>
                </Grid>
              </Grid>
            </Paper>
          )}

          {currentView === "billing" && (
            <Paper sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5">Billing & Claims</Typography>
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon aria-label="Add new billing record" />}
                  aria-label="Add Billing Record"
                  onClick={() => setAddBillingDialogOpen(true)}
                >
                  Add Billing Record
                </Button>
              </Box>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" color="primary">Total Billed</Typography>
                    <Typography variant="h4">${billingStats.total.toFixed(2)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {billingRecords.length} billing records
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" color="success.main">Paid</Typography>
                    <Typography variant="h4">${billingStats.paid.toFixed(2)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {billingStats.total ? Math.round((billingStats.paid / billingStats.total) * 100) : 0}% collected
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" color="warning.main">Pending</Typography>
                    <Typography variant="h4">${billingStats.pending.toFixed(2)}</Typography>
                    <Typography variant="caption" color="text.secondary">Outstanding claims</Typography>
                  </Paper>
                </Grid>
              </Grid>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Patient</strong></TableCell>
                      <TableCell><strong>Service</strong></TableCell>
                      <TableCell align="right"><strong>Billed</strong></TableCell>
                      <TableCell align="right"><strong>Paid</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {billingRecords.slice(0, 20).map((record) => {
                      const patient = patients.find((p) => p.id === record.patient_id);
                      return (
                        <TableRow key={record.id} hover>
                          <TableCell>{record.service_date}</TableCell>
                          <TableCell>{patient?.name || `Patient #${record.patient_id}`}</TableCell>
                          <TableCell>
                            <Typography variant="body2">{record.description}</Typography>
                            <Typography variant="caption" color="text.secondary">{record.service_code}</Typography>
                          </TableCell>
                          <TableCell align="right">${record.amount?.toFixed(2)}</TableCell>
                          <TableCell align="right">${record.insurance_paid?.toFixed(2)}</TableCell>
                          <TableCell>
                            <Chip
                              label={record.status}
                              size="small"
                              color={record.status === "paid" ? "success" : record.status === "denied" ? "error" : "warning"}
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {currentView === "reports" && (
            <Box>
              <Typography variant="h5" mb={3}>Reports & Analytics</Typography>

              <Paper sx={{ p: 3, mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    <AssessmentIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                    Patients by Organization
                  </Typography>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    startIcon={<AssessmentIcon />} 
                    onClick={() => fetchReport("patients-by-organization")}
                    aria-label="View patients by organization report"
                  >
                    View Details
                  </Button>
                </Box>
                <Grid container spacing={2}>
                  {organizations.map((org) => {
                    const count = patients.filter((p) => p.organization_id === org.id).length;
                    return (
                      <Grid item xs={12} sm={4} key={org.id}>
                        <Paper sx={{ p: 2, bgcolor: "primary.main", color: "white" }}>
                          <Typography variant="h4" fontWeight="bold">{count}</Typography>
                          <Typography variant="body2">{org.name}</Typography>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>

              <Paper sx={{ p: 3, mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    <ListAltIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                    Outstanding Care Items
                  </Typography>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    startIcon={<AssessmentIcon />} 
                    onClick={() => fetchReport("care-gaps-summary")}
                    aria-label="View outstanding care items report"
                  >
                    View Details
                  </Button>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h4" color="error.main" fontWeight="bold">
                        {reportSummary?.pending_tasks ?? "–"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Pending Tasks</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h4" color="warning.main" fontWeight="bold">
                        {reportSummary?.overdue_screenings ?? "–"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Overdue Screenings</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h4" color="info.main" fontWeight="bold">
                        {reportSummary?.chronic_condition_patients ?? "–"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Chronic Conditions</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h4" color="success.main" fontWeight="bold">
                        {reportSummary?.active_medications ?? "–"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Active Medications</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>

              <Paper sx={{ p: 3, mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    <AssessmentIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                    Overdue Preventive Care Items
                  </Typography>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    startIcon={<AssessmentIcon />} 
                    onClick={() => fetchReport("overdue-preventive-care")}
                    aria-label="View overdue preventive care full report"
                  >
                    View Full Report
                  </Button>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Patient</strong></TableCell>
                        <TableCell><strong>Organization</strong></TableCell>
                        <TableCell><strong>Overdue Items</strong></TableCell>
                        <TableCell align="right"><strong>Action</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(reportSummary?.overdue_by_patient || [])
                        .slice(0, 10)
                        .map((row) => {
                          const patient = { id: row.patient_id, name: row.patient_name };
                          const overdueCount = row.overdue_count;
                          const org = organizations.find((o) => o.id === row.organization_id);
                          return (
                            <TableRow key={patient.id}>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <PersonIcon fontSize="small" color="primary" />
                                  {patient.name}
                                </Box>
                              </TableCell>
                              <TableCell>{org?.name || "N/A"}</TableCell>
                              <TableCell>
                                <Chip label={`${overdueCount} overdue`} color="warning" size="small" />
                              </TableCell>
                              <TableCell align="right">
                                <Button size="small" onClick={() => {
                                  navigate(`/patient/${patient.id}`);
                                }}
                                aria-label={`View patient ${patient.name}`}
                                >
                                  View Patient
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      {reportSummary && (reportSummary.overdue_by_patient || []).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography color="text.secondary">No overdue preventive care items</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          )}
        </Box>

        <Dialog open={openOrgDialog} onClose={handleCloseOrgDialog} maxWidth="md" fullWidth>
          <DialogTitle>{selectedOrg ? "Edit Organization" : "Add New Organization"}</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Organization Name"
                value={orgFormData.name || ""}
                onChange={(e) => setOrgFormData({ ...orgFormData, name: e.target.value })}
                margin="normal"
                required
                aria-label="Organization name"
                data-field-name="organization_name"
                data-testid="organization-name-input"
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Organization Type</InputLabel>
                <Select
                  value={orgFormData.type || "hospital"}
                  onChange={(e) => setOrgFormData({ ...orgFormData, type: e.target.value })}
                  aria-label="Organization type selection"
                  data-field-name="organization_type"
                  data-testid="organization-type-select"
                >
                  <MenuItem value="hospital">Hospital</MenuItem>
                  <MenuItem value="clinic">Clinic</MenuItem>
                  <MenuItem value="urgent_care">Urgent Care</MenuItem>
                  <MenuItem value="specialty_practice">Specialty Practice</MenuItem>
                  <MenuItem value="nursing_home">Nursing Home</MenuItem>
                  <MenuItem value="home_health">Home Health</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Address"
                value={orgFormData.address || ""}
                onChange={(e) => setOrgFormData({ ...orgFormData, address: e.target.value })}
                margin="normal"
                multiline
                rows={2}
                aria-label="Organization address"
                data-field-name="organization_address"
                data-testid="organization-address-input"
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={orgFormData.phone || ""}
                    onChange={(e) => setOrgFormData({ ...orgFormData, phone: e.target.value })}
                    margin="normal"
                    aria-label="Organization phone"
                    data-field-name="organization_phone"
                    data-testid="organization-phone-input"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={orgFormData.email || ""}
                    onChange={(e) => setOrgFormData({ ...orgFormData, email: e.target.value })}
                    margin="normal"
                    aria-label="Organization email"
                    data-field-name="organization_email"
                    data-testid="organization-email-input"
                  />
                </Grid>
              </Grid>
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Feature Flags
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Enable or disable features for this organization
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={orgFormData.feature_flags?.care_plans || false}
                    onChange={(e) =>
                      setOrgFormData({
                        ...orgFormData,
                        feature_flags: { ...orgFormData.feature_flags, care_plans: e.target.checked },
                      })
                    }
                  />
                }
                label="Care Plans Module"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={orgFormData.feature_flags?.billing || false}
                    onChange={(e) =>
                      setOrgFormData({
                        ...orgFormData,
                        feature_flags: { ...orgFormData.feature_flags, billing: e.target.checked },
                      })
                    }
                  />
                }
                label="Billing & Claims"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={orgFormData.feature_flags?.reports || false}
                    onChange={(e) =>
                      setOrgFormData({
                        ...orgFormData,
                        feature_flags: { ...orgFormData.feature_flags, reports: e.target.checked },
                      })
                    }
                  />
                }
                label="Reporting System"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={orgFormData.feature_flags?.ai_assistant || false}
                    onChange={(e) =>
                      setOrgFormData({
                        ...orgFormData,
                        feature_flags: { ...orgFormData.feature_flags, ai_assistant: e.target.checked },
                      })
                    }
                  />
                }
                label="AI Assistant"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={orgFormData.feature_flags?.preventive_care || false}
                    onChange={(e) =>
                      setOrgFormData({
                        ...orgFormData,
                        feature_flags: { ...orgFormData.feature_flags, preventive_care: e.target.checked },
                      })
                    }
                  />
                }
                label="Preventive Care Tracking"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseOrgDialog}>Cancel</Button>
            <Button onClick={handleSaveOrganization} variant="contained" color="primary">
              {selectedOrg ? "Update" : "Create"} Organization
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openReportDialog} onClose={() => setOpenReportDialog(false)} maxWidth="lg" fullWidth>
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">
                {reportType === "patients-by-organization" && "Patients by Organization Report"}
                {reportType === "overdue-preventive-care" && "Overdue Preventive Care Report"}
                {reportType === "care-gaps-summary" && "Outstanding Items Summary"}
              </Typography>
              <Button 
                startIcon={<AssessmentIcon />} 
                variant="outlined" 
                onClick={exportReportToCSV}
                aria-label="Export report to CSV"
              >
                Export CSV
              </Button>
            </Box>
          </DialogTitle>
          <DialogContent>
            {reportData ? (
              <Box sx={{ mt: 2 }}>
                {reportType === "patients-by-organization" && Array.isArray(reportData) && (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Organization</strong></TableCell>
                          <TableCell align="right"><strong>Patient Count</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportData.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <BusinessIcon color="primary" fontSize="small" />
                                {row.organization_name}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Chip label={row.patient_count} color="primary" />
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell><strong>Total</strong></TableCell>
                          <TableCell align="right"><strong>{reportData.reduce((sum, row) => sum + row.patient_count, 0)}</strong></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {reportType === "overdue-preventive-care" && Array.isArray(reportData) && (
                  <>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Found {reportData.length} overdue preventive care items
                    </Alert>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Patient</strong></TableCell>
                            <TableCell><strong>Care Item</strong></TableCell>
                            <TableCell><strong>Due Date</strong></TableCell>
                            <TableCell><strong>Days Overdue</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {reportData.map((row, index) => {
                            const dueDate = new Date(row.due_date);
                            const today = new Date();
                            const daysOverdue = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
                            return (
                              <TableRow key={index}>
                                <TableCell>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <PersonIcon fontSize="small" color="primary" />
                                    {row.patient_name}
                                  </Box>
                                </TableCell>
                                <TableCell>{row.care_item}</TableCell>
                                <TableCell>{new Date(row.due_date).toLocaleDateString()}</TableCell>
                                <TableCell>
                                  <Chip label={`${daysOverdue} days`} color={daysOverdue > 90 ? "error" : "warning"} size="small" />
                                </TableCell>
                                <TableCell>
                                  <Chip label={row.status} size="small" color="warning" />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}

                {reportType === "care-gaps-summary" && typeof reportData === "object" && (
                  <Grid container spacing={2}>
                    {Object.entries(reportData).map(([key, value]) => (
                      <Grid item xs={12} sm={6} md={4} key={key}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            {key.replace(/_/g, " ").toUpperCase()}
                          </Typography>
                          <Typography variant="h4" color="primary">
                            {value}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}

                {!reportData || (Array.isArray(reportData) && reportData.length === 0) ? (
                  <Alert severity="info">No data available for this report</Alert>
                ) : null}
              </Box>
            ) : (
              <Alert severity="info">No data available for this report</Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenReportDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Menu
          anchorEl={notificationMenuAnchor}
          open={Boolean(notificationMenuAnchor)}
          onClose={() => setNotificationMenuAnchor(null)}
          PaperProps={{ sx: { width: 360, maxHeight: 400 } }}
        >
          <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="h6">Notifications</Typography>
            {unreadCount > 0 && (
              <Typography variant="caption" color="text.secondary">
                {unreadCount} unread
              </Typography>
            )}
          </Box>

          {notifications.length > 0 ? (
            <List sx={{ pt: 0 }}>
              {notifications.slice(0, 10).map((notification) => (
                <ListItem
                  key={notification.id}
                  sx={{
                    bgcolor: notification.is_read ? "transparent" : "action.hover",
                    borderLeft: notification.is_read ? "none" : "4px solid",
                    borderColor: "primary.main",
                  }}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight={notification.is_read ? 400 : 600}>
                          {notification.title}
                        </Typography>
                        {!notification.is_read && <Chip label="New" size="small" color="primary" />}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" display="block">
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(notification.created_at).toLocaleString()}
                        </Typography>
                      </Box>
                    }
                  />
                  {!notification.is_read && (
                    <IconButton 
                      size="small" 
                      onClick={() => handleMarkNotificationRead(notification.id)}
                      aria-label={`Mark notification as read: ${notification.title}`}
                    >
                      <PersonIcon aria-label={`Mark notification as read: ${notification.title}`} fontSize="small" />
                    </IconButton>
                  )}
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <NotificationsIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
              <Typography color="text.secondary">No notifications</Typography>
            </Box>
          )}
        </Menu>

        <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleSnackbarClose}>
          <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        <AddPatientDialog
          open={addPatientDialogOpen}
          onClose={() => setAddPatientDialogOpen(false)}
          onPatientAdded={handlePatientAdded}
        />

        <AddBillingRecordDialog
          open={addBillingDialogOpen}
          onClose={() => setAddBillingDialogOpen(false)}
          onRecordAdded={() => {
            loadBillingData();
            setSnackbar({ open: true, message: "Billing record added", severity: "success" });
          }}
          patients={patients}
          apiBase={API}
        />
      </Box>
  );
}
